import { stringify } from './stringify';

/**
 * Shared image dimensions (logical / CSS pixels) so call sites request a small,
 * consistent set of sizes — fewer distinct values means fewer cached CDN variants
 * and a better cache hit rate.
 *
 * Each value is a single dimension; pass it as `width` or `height` to
 * resizedImage() depending on the element (resizedImage() multiplies by
 * devicePixelRatio, capped at 3x, so these are pre-DPR sizes). Other options
 * (quality, background, skipVectorImages) stay at the call site.
 *
 * Ordered small -> large.
 */
export const ImageSizes = {
  THUMBNAIL: 50,
  CARD: 200,
  PANEL: 500,
  POSTER: 1000,
};

export const DEFAULT_RESIZE_OPTIONS_PRODUCT_SMALL = { height: ImageSizes.PANEL, quality: 80 };

const FALLBACK_IMAGE_TRANSFORMER_BASE_URL = 'https://cdn.liveshopping.bambuser.com/imageTransformer';
const resolveProtocolRelativeUrl = (url, baseUrl) => {
  try {
    return new URL(url, baseUrl).href;
  } catch {
    console.warn('Failed to resolve protocol-relative image URL', url);
    return '';
  }
};

let imageTransformerBaseUrl = null;
try {
  // See if we can't find the client-config for project.
  // Because this function is a shared utils functions the client config might not always
  // be in the same location, and in some setups it might not even be defined.
  imageTransformerBaseUrl = process.env.IMAGE_TRANSFORMER_BASE_URL || FALLBACK_IMAGE_TRANSFORMER_BASE_URL;
} catch (error) {
  console.log(error);
  // If worse comes to worse, use the lcpro environments image transformer served via CF
  imageTransformerBaseUrl = FALLBACK_IMAGE_TRANSFORMER_BASE_URL;
}

// Detect if browser supports WebP
let browserSupportsWebP = false;
if (window && window.Image) {
  (function () {
    const img = new window.Image();
    img.onload = () => {
      browserSupportsWebP = !!(img.height > 0 && img.width > 0);
    };
    img.onerror = () => {
      browserSupportsWebP = false;
    };
    img.src = 'data:image/webp;base64,UklGRiIAAABXRUJQVlA4IBYAAAAwAQCdASoBAAEADsD+JaQAA3AAAAAA';
  })();
}

/**
 * Takes an URL for an image along with some options and
 * returns an URL for our imageTransform cloud function
 *
 * Available options are:
 *   quality (1-100)
 *   fit
 *   width
 *   height
 *   background
 *
 * http://sharp.pixelplumbing.com/en/stable/api-resize/
 * shows the full spec for the options above.
 */
export function resizedImage(rawUrl, opts = {}) {
  if (typeof rawUrl !== 'string') return ''; // Must be a string
  const url = rawUrl.startsWith('//')
    ? resolveProtocolRelativeUrl(rawUrl, window.location.href)
    : rawUrl;
  if (url.match(/^data:image\//)) return url; // Don't transform data-urls
  if (!url.match(/:\/\//)) return ''; // Must look like an url
  if (typeof window.btoa !== 'function') return url; // btoa() has superb browser support, but better safe than sorry
  const preFileExtension = url.split('.').pop().split(/#|\?/)[0].toLocaleLowerCase();
  if (opts.skipVectorImages && preFileExtension === 'svg') return url; // Skip vector images
  const MAX_SIDE = 1000; // imageTransformer rejects larger dimensions
  const dpr = Math.min(window.devicePixelRatio || 1, 3);
  const scaledHeight = opts.height ? Math.min(Math.ceil(opts.height * dpr), MAX_SIDE) : undefined;
  const scaledWidth = opts.width ? Math.min(Math.ceil(opts.width * dpr), MAX_SIDE) : undefined;
  const spec = stringify({
    ...opts,
    url,
    ...(scaledHeight !== undefined && { height: scaledHeight }),
    ...(scaledWidth !== undefined && { width: scaledWidth }),
  });
  let extension;
  // No WEBP transformation for GIF files, otherwise we loose the animation
  // See functions/endpoints/imageTransformer.js file.
  if (preFileExtension === 'gif') {
    extension = 'gif';
  } else if (browserSupportsWebP) {
    extension = 'webp';
  } else {
    switch (preFileExtension) {
      case 'png':
        extension = 'png';
        break;
      /* case 'gif': // TO BE UNCOMMENTED ONCE WEBP TO GIF CONVERSION IS IMPLEMENTED
        extension = 'gif';
        break; */
      default:
        extension = 'jpg';
        break;
    }
  }
  return `${imageTransformerBaseUrl}/i/${window.btoa(spec)}.${extension}`;
}

const imageCache = new Map();

export function cacheImageUrl(url) {
  if (!url) return;
  if (!imageCache.has(url) && window.Image) {
    const img = new window.Image();
    img.onload = () => {
      imageCache.set(url, true);
    };
    imageCache.set(url, img);
    img.src = url;
  }
}
